'use client'

import { useState } from 'react'
import { Button } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Copy } from 'lucide-react'
import type { SceneParagraph } from '@creanizer/types'

interface SceneTextViewerProps {
  text: string
  paragraphs: SceneParagraph[]
}

export function SceneTextViewer({ text, paragraphs }: SceneTextViewerProps) {
  const [viewMode, setViewMode] = useState<'full' | 'paragraph'>('full')
  const [selectedParagraph, setSelectedParagraph] = useState<number>(0)
  const [fontSize, setFontSize] = useState<'small' | 'medium' | 'large'>('medium')
  
  const fontSizeClass = {
    small: 'text-sm',
    medium: 'text-base',
    large: 'text-lg'
  }[fontSize]

  const formatText = (text: string) => {
    // 改行を処理
    return text.split('\n').map((line, index) => (
      <span key={index}>
        {line}
        {index < text.split('\n').length - 1 && <br />}
      </span>
    ))
  }

  return (
    <div className="space-y-4">
      {/* ビューアコントロール */}
      <div className="flex gap-2 items-center">
        <Button
          variant={viewMode === 'full' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('full')}
        >
          全文表示
        </Button>
        <Button
          variant={viewMode === 'paragraph' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('paragraph')}
        >
          段落表示
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <Select value={fontSize} onValueChange={(value) => setFontSize(value as any)}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="small">小</SelectItem>
              <SelectItem value="medium">中</SelectItem>
              <SelectItem value="large">大</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigator.clipboard.writeText(text)}
          >
            <Copy className="h-4 w-4 mr-1" />
            全文コピー
          </Button>
        </div>
      </div>
      
      {/* テキスト表示 */}
      <div className={`prose prose-slate max-w-none ${fontSizeClass}`}>
        {viewMode === 'full' ? (
          <div className="whitespace-pre-wrap p-6 bg-background border rounded-lg min-h-[400px]">
            {formatText(text)}
          </div>
        ) : (
          <div>
            {/* 段落ナビゲーション */}
            <div className="flex gap-1 mb-4 flex-wrap">
              {paragraphs.map((p, i) => (
                <Button
                  key={i}
                  size="sm"
                  variant={selectedParagraph === i ? 'default' : 'outline'}
                  onClick={() => setSelectedParagraph(i)}
                  className="min-w-[40px]"
                >
                  {i + 1}
                </Button>
              ))}
            </div>
            {/* 選択段落表示 */}
            {paragraphs[selectedParagraph] && (
              <div className="p-6 border rounded-lg bg-background">
                <div className="text-sm text-muted-foreground mb-4 flex items-center justify-between">
                  <div>
                    段落 {selectedParagraph + 1} / {paragraphs.length}
                    {paragraphs[selectedParagraph].type && (
                      <span className="ml-2 px-2 py-1 bg-muted rounded text-xs">
                        {paragraphs[selectedParagraph].type === 'dialogue' ? '会話' :
                         paragraphs[selectedParagraph].type === 'action' ? 'アクション' :
                         paragraphs[selectedParagraph].type === 'description' ? '描写' :
                         paragraphs[selectedParagraph].type === 'internal' ? '内面' :
                         paragraphs[selectedParagraph].type === 'transition' ? '転換' :
                         paragraphs[selectedParagraph].type}
                      </span>
                    )}
                    {paragraphs[selectedParagraph].speaker && (
                      <span className="ml-2">
                        話者: {paragraphs[selectedParagraph].speaker}
                      </span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigator.clipboard.writeText(paragraphs[selectedParagraph].text)}
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    コピー
                  </Button>
                </div>
                <div className="whitespace-pre-wrap">
                  {formatText(paragraphs[selectedParagraph].text)}
                </div>
              </div>
            )}
            
            {/* ナビゲーションボタン */}
            <div className="flex justify-between mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedParagraph(Math.max(0, selectedParagraph - 1))}
                disabled={selectedParagraph === 0}
              >
                前の段落
              </Button>
              <span className="text-sm text-muted-foreground">
                {selectedParagraph + 1} / {paragraphs.length}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedParagraph(Math.min(paragraphs.length - 1, selectedParagraph + 1))}
                disabled={selectedParagraph === paragraphs.length - 1}
              >
                次の段落
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}